/**
 * Component: SemanticSearchPage
 * SpecReferenceID: SEARCH-SEMANTIC-V1
 * Requirements: SEARCH-SEMANTIC-V1-R01, SEARCH-SEMANTIC-V1-R02, SEARCH-SEMANTIC-V1-R03,
 *               SEARCH-SEMANTIC-V1-R05, SEARCH-SEMANTIC-V1-R08, SEARCH-SEMANTIC-V1-R09,
 *               FRONTEND-WEB-V1-R02, FRONTEND-WEB-V1-R09,
 *               FRONTEND-WEB-V1-R10, FRONTEND-WEB-V1-R13
 *
 * Standalone semantic search interface for retrieval-only search.
 * Displays top N results without LLM generation.
 *
 * FRONTEND-WEB-V1-R02: Collect user input via text or optional audio.
 * SEARCH-SEMANTIC-V1-R03: Results with title, section, date, score, metadata, open action.
 * SEARCH-SEMANTIC-V1-R05: Enable users to open content from results.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useRef } from 'react';
import { Search, Loader2, AlertCircle } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';
import { trackUsageAction } from '@/lib/usage-tracking';
import { useBranding, useIsFeatureEnabled } from '@/contexts';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserInputArea } from '@/components/input/UserInputArea';
import {
  SearchFilterSidebar,
  SearchFilterMobileButton,
  INITIAL_FILTERS,
  type SearchFilters,
  type Facet,
} from './components/SearchFilterSidebar';
import { toFilterRequest, hasActiveFilters } from './components/search-filter-types';
import { useFilterAutoSearch } from './useFilterAutoSearch';
import { SearchPagination } from './components/SearchPagination';
import { SearchResultCard } from './components/SearchResultCard';
import { ResultDetailPanel } from './components/ResultDetailPanel';
import { normalizeQuotes } from './components/search-result-utils';
import { SafeHtml } from '@/components/SafeHtml';

const DEFAULT_PAGE_SIZE = 20;

// API Types

/**
 * SEARCH-SEMANTIC-V1-R03: Extractive answer from Azure AI Search
 * semantic query, displayed above search results.
 */
interface SearchAnswer {
  text: string;
  highlights: string;
  score: number;
  key: string;
}

interface SearchResult {
  document_id: string;
  title: string;
  content: string;
  score: number;
  semantic_score: number | null;
  keyword_score: number | null;
  captions: string[];
  highlights: Record<string, string[]>;
  metadata: Record<string, unknown>;
  source_url: string | null;
}

interface SearchResponse {
  answers: SearchAnswer[];
  results: SearchResult[];
  total_count: number | null;
  facets: Facet[];
  query: string;
  mode: string;
}

type SearchMode = 'hybrid' | 'semantic';


/**
 * SemanticSearchPage Component
 *
 * SEARCH-SEMANTIC-V1-R01: Standalone UI interface for semantic search.
 * SEARCH-SEMANTIC-V1-R02: Execute semantic/hybrid search and return top N.
 * SEARCH-SEMANTIC-V1-R03: Display results with title, excerpt, score.
 */
export function SemanticSearchPage(): React.ReactElement {
  // Bug #3979: Read search mode from admin config instead of local state
  const { branding } = useBranding();
  const mode: SearchMode = (branding.semanticSearchDefaultMode as SearchMode) || 'hybrid';
  // SPEECH-V1-R02 / Bug #4340: hide the mic affordance entirely when the
  // speech module is not enabled for this deployment.
  const isSpeechEnabled = useIsFeatureEnabled('speech');

  // State
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [answers, setAnswers] = useState<SearchAnswer[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsTopRef = useRef<HTMLDivElement>(null);
  // Bug #3878: State for detail panel
  const [detailSectionId, setDetailSectionId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // SEARCH-SEMANTIC-V1-R08: facets returned by the backend drive the
  // capability-driven refine controls. Filtering is applied server-side, so
  // results are rendered exactly as returned (no client-side post-filtering).
  const [facets, setFacets] = useState<Facet[]>([]);

  /**
   * Execute search request with pagination.
   * SEARCH-SEMANTIC-V1-R02: Execute semantic or hybrid search.
   * SEARCH-SEMANTIC-V1-R03: Paginated result presentation.
   */
  const handleSearch = useCallback(
    async (e?: React.FormEvent, page = 1) => {
      e?.preventDefault();

      if (!query.trim()) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setHasSearched(true);

      try {
        const skip = (page - 1) * DEFAULT_PAGE_SIZE;
        // SEARCH-SEMANTIC-V1-R08/R09: send the user's refine selections so the
        // backend applies them server-side (filter + recency scoring profile).
        const response = await api.post<SearchResponse>('/search/semantic', {
          query: normalizeQuotes(query.trim()),
          mode,
          include_captions: true,
          top_n: DEFAULT_PAGE_SIZE,
          skip,
          ...toFilterRequest(filters),
          prioritize_recent: filters.prioritizeRecent,
        });

        setResults(response.data.results);
        setAnswers(response.data.answers ?? []);
        setTotalCount(response.data.total_count);
        setFacets(response.data.facets ?? []);
        setCurrentPage(page);

        // FRONTEND-WEB-V1-R10: Track search query for usage metrics
        void trackUsageAction('search_query', {
          query_text: query.trim(),
          mode,
        });
      } catch (err) {
        setError(getErrorMessage(err));
        setResults([]);
        setAnswers([]);
        setTotalCount(null);
        setFacets([]);
      } finally {
        setIsLoading(false);
      }
    },
    [query, mode, filters]
  );

  // SEARCH-SEMANTIC-V1-R08 (Bug #5052): server-side filtering re-runs the search
  // (reset to page 1) when a refine selection changes, debounced so multi-select
  // coalesces into one request. Only fires once a search has been performed.
  useFilterAutoSearch(filters, hasSearched, () => void handleSearch(undefined, 1));

  const handlePageChange = useCallback(
    (page: number) => {
      void handleSearch(undefined, page);
      resultsTopRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [handleSearch]
  );

  /**
   * Handle link open: open detail panel for the clicked result.
   * Bug #3878: Opens detail panel with section content.
   * COUNTER tracking is handled by SearchResultCard internally.
   */
  const handleLinkOpen = useCallback(
    (_documentId: string, _rankingPosition: number, guidelineSectionId: number | null) => {
      if (guidelineSectionId != null) {
        setDetailSectionId(guidelineSectionId);
        setDetailOpen(true);
      }
    },
    []
  );

  // SEARCH-SEMANTIC-V1-R08 (Bug #5053): keep the refine sidebar (and Clear all)
  // visible when a search has run and either there are results OR filters are
  // active, so a filter that returns zero results can still be cleared.
  const showFilters =
    hasSearched && (results.length > 0 || hasActiveFilters(filters));

  return (
    <div className="mx-auto max-w-5xl">
      {/* Page Header */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Semantic Search</h1>
        <p className="mt-2 text-muted-foreground">
          Find relevant guideline sections using natural language queries.
          You can type or use voice input.
        </p>
      </header>

      {/* Search Form - FRONTEND-WEB-V1-R02: Text and optional audio input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSearch();
        }}
        className="mb-8 space-y-4"
        role="search"
        aria-label="Semantic search"
      >
        {/* FRONTEND-WEB-V1-R13: Responsive layout - stack on mobile, row on desktop */}
        <div className="flex flex-col gap-4 md:flex-row">
          {/* FRONTEND-WEB-V1-R02: UserInputArea with audio support */}
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10"
              aria-hidden="true"
            />
            <div className="pl-8">
              <UserInputArea
                value={query}
                onChange={setQuery}
                onSubmit={() => void handleSearch()}
                placeholder="Ask a guideline related question..."
                disabled={isLoading}
                enableAudio={isSpeechEnabled}
                multiline={false}
                ariaLabel="Search query"
              />
            </div>
          </div>
          {/* Bug #3979: Search mode selector removed — mode is now read from admin config */}
          <Button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="min-h-touch"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </Button>
        </div>
      </form>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* SEARCH-SEMANTIC-V1-R08: Mobile filter button (shown above results) */}
      {showFilters && (
        <div className="md:hidden">
          <SearchFilterMobileButton
            filters={filters}
            onFiltersChange={setFilters}
            onReset={() => setFilters(INITIAL_FILTERS)}
            facets={facets}
            resultCount={results.length}
          />
        </div>
      )}

      {/* Results area: sidebar on desktop + results */}
      <div ref={resultsTopRef} className={showFilters ? 'md:flex md:gap-6' : ''}>
        {/* Desktop sidebar */}
        {showFilters && (
          <aside className="hidden md:block w-56 shrink-0" aria-label="Search filters">
            <div className="sticky top-4">
              <SearchFilterSidebar
                filters={filters}
                onFiltersChange={setFilters}
                onReset={() => setFilters(INITIAL_FILTERS)}
                facets={facets}
                resultCount={results.length}
              />
            </div>
          </aside>
        )}

        <div className="min-w-0 flex-1">
          {/* Results Summary */}
          {hasSearched && !isLoading && !error && (
            <div className="mb-4 text-sm text-muted-foreground" aria-live="polite">
              {results.length > 0 ? (
                <span>Showing {results.length} result{results.length !== 1 ? 's' : ''}</span>
              ) : (
                <span>No results found for &ldquo;{query}&rdquo;</span>
              )}
            </div>
          )}

          {/* SEARCH-SEMANTIC-V1-R03: Extractive answer text above first result (Bug #3869: no card styling) */}
          {answers.length > 0 && currentPage === 1 && (
            <div className="mb-4" role="region" aria-label="Semantic answer">
              {answers.map((answer, idx) => (
                <SafeHtml
                  key={idx}
                  html={answer.highlights || answer.text}
                  className="text-sm [&_em]:font-semibold [&_em]:text-primary [&_em]:not-italic [&_em]:bg-yellow-100 [&_em]:dark:bg-yellow-900/40 [&_em]:px-0.5 [&_em]:rounded"
                />
              ))}
            </div>
          )}

          {/* Results List */}
          <section
            aria-label="Search results"
            className="space-y-4"
            aria-busy={isLoading}
          >
            {results.map((result, index) => (
              <SearchResultCard
                key={result.document_id}
                result={result}
                rank={(currentPage - 1) * DEFAULT_PAGE_SIZE + index + 1}
                onLinkOpen={handleLinkOpen}
                query={query}
                searchMode="semantic"
              />
            ))}
          </section>

          {/* Pagination: Only rendered when total_count is available
              (Simple Search). Semantic/hybrid search suppresses total_count
              because similarity scoring makes it misleading (Bug #3862). */}
          {totalCount !== null && totalCount > 0 && (
            <SearchPagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={handlePageChange}
              isLoading={isLoading}
            />
          )}

          {/* Empty State */}
          {hasSearched && !isLoading && results.length === 0 && !error && (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="mx-auto mb-4 h-12 w-12 opacity-50" aria-hidden="true" />
              <p className="text-lg">No results found</p>
              <p className="mt-2">
                Try adjusting your search terms or filters.
              </p>
            </div>
          )}

          {/* Initial State */}
          {!hasSearched && (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="mx-auto mb-4 h-12 w-12 opacity-50" aria-hidden="true" />
              <p className="text-lg">Enter a query to search</p>
              <p className="mt-2">
                Use natural language to find relevant clinical guideline sections.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bug #3878: Detail panel for View Full Result */}
      <ResultDetailPanel
        guidelineSectionId={detailSectionId}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
}

export default SemanticSearchPage;
